var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createContext, useContext } from 'react';
import { Button } from 'antd';
import { DeleteOutlined, DownOutlined, UpOutlined, PlusOutlined, MenuOutlined, CopyOutlined, } from '@ant-design/icons';
import { useField, useFieldSchema, RecordScope, RecordsScope, } from '@formily/react';
import { isValid, clone } from '@formily/shared';
import { SortableHandle } from 'react-sortable-hoc';
import { usePrefixCls } from '../__builtins__';
import cls from 'classnames';
var ArrayBaseContext = createContext(null);
var ItemContext = createContext(null);
var takeRecord = function (val, index) {
    return typeof val === 'function' ? val(index) : val;
};
var useArray = function () {
    return useContext(ArrayBaseContext);
};
var useIndex = function (index) {
    var ctx = useContext(ItemContext);
    return ctx ? ctx.index : index;
};
var useRecord = function (record) {
    var ctx = useContext(ItemContext);
    return takeRecord(ctx ? ctx.record : record, ctx === null || ctx === void 0 ? void 0 : ctx.index);
};
var getSchemaDefaultValue = function (schema) {
    if ((schema === null || schema === void 0 ? void 0 : schema.type) === 'array')
        return [];
    if ((schema === null || schema === void 0 ? void 0 : schema.type) === 'object')
        return {};
    if ((schema === null || schema === void 0 ? void 0 : schema.type) === 'void') {
        for (var key in schema.properties) {
            var value = getSchemaDefaultValue(schema.properties[key]);
            if (isValid(value))
                return value;
        }
    }
};
var getDefaultValue = function (defaultValue, schema) {
    if (isValid(defaultValue))
        return clone(defaultValue);
    if (Array.isArray(schema === null || schema === void 0 ? void 0 : schema.items))
        return getSchemaDefaultValue(schema === null || schema === void 0 ? void 0 : schema.items[0]);
    return getSchemaDefaultValue(schema === null || schema === void 0 ? void 0 : schema.items);
};
export var ArrayBase = function (props) {
    var field = useField();
    var schema = useFieldSchema();
    return (React.createElement(RecordsScope, { getRecords: function () { return field.value; } },
        React.createElement(ArrayBaseContext.Provider, { value: { field: field, schema: schema, props: props } }, props.children)));
};
ArrayBase.Item = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    var index = props.index;
    var record = takeRecord(props.record, props.index);
    return (React.createElement(ItemContext.Provider, { value: props },
        React.createElement(RecordScope, { getIndex: function () { return index; }, getRecord: function () { return record; } }, children)));
};
var SortHandle = SortableHandle(function (props) {
    var prefixCls = usePrefixCls('formily-array-base');
    return (React.createElement(MenuOutlined, __assign({}, props, { className: cls("".concat(prefixCls, "-sort-handle"), props.className), style: __assign({}, props.style) })));
});
ArrayBase.SortHandle = function (props) {
    var _a;
    var array = useArray();
    if (!array)
        return null;
    if (((_a = array.field) === null || _a === void 0 ? void 0 : _a.pattern) !== 'editable')
        return null;
    return React.createElement(SortHandle, __assign({}, props));
};
ArrayBase.Index = function (props) {
    var index = useIndex();
    var prefixCls = usePrefixCls('formily-array-base');
    return (React.createElement("span", __assign({}, props, { className: "".concat(prefixCls, "-index") }),
        "#",
        index + 1,
        "."));
};
ArrayBase.Addition = function (props) {
    var _a, _b;
    var self = useField();
    var array = useArray();
    var prefixCls = usePrefixCls('formily-array-base');
    if (!array)
        return null;
    if (((_a = array.field) === null || _a === void 0 ? void 0 : _a.pattern) !== 'editable' &&
        ((_b = array.field) === null || _b === void 0 ? void 0 : _b.pattern) !== 'disabled')
        return null;
    return (React.createElement(Button, __assign({ type: "dashed", block: true }, props, { disabled: self === null || self === void 0 ? void 0 : self.disabled, className: cls("".concat(prefixCls, "-addition"), props.className), onClick: function (e) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            if ((_a = array.props) === null || _a === void 0 ? void 0 : _a.disabled)
                return;
            var defaultValue = getDefaultValue(props.defaultValue, array.schema);
            if (props.method === 'unshift') {
                (_c = (_b = array.field) === null || _b === void 0 ? void 0 : _b.unshift) === null || _c === void 0 ? void 0 : _c.call(_b, defaultValue);
                (_e = (_d = array.props) === null || _d === void 0 ? void 0 : _d.onAdd) === null || _e === void 0 ? void 0 : _e.call(_d, 0);
            }
            else {
                (_g = (_f = array.field) === null || _f === void 0 ? void 0 : _f.push) === null || _g === void 0 ? void 0 : _g.call(_f, defaultValue);
                (_j = (_h = array.props) === null || _h === void 0 ? void 0 : _h.onAdd) === null || _j === void 0 ? void 0 : _j.call(_h, ((_l = (_k = array === null || array === void 0 ? void 0 : array.field) === null || _k === void 0 ? void 0 : _k.value) === null || _l === void 0 ? void 0 : _l.length) - 1);
            }
            if (props.onClick) {
                props.onClick(e);
            }
        }, icon: React.createElement(PlusOutlined, null) }), props.title || self.title));
};
ArrayBase.Copy = React.forwardRef(function (props, ref) {
    var _a;
    var self = useField();
    var array = useArray();
    var index = useIndex(props.index);
    var prefixCls = usePrefixCls('formily-array-base');
    if (!array)
        return null;
    if (((_a = array.field) === null || _a === void 0 ? void 0 : _a.pattern) !== 'editable')
        return null;
    return (React.createElement(CopyOutlined, __assign({}, props, { className: cls("".concat(prefixCls, "-copy"), (self === null || self === void 0 ? void 0 : self.disabled) ? "".concat(prefixCls, "-copy-disabled") : '', props.className), ref: ref, onClick: function (e) {
            var _a, _b, _c, _d, _e, _f;
            if (self === null || self === void 0 ? void 0 : self.disabled)
                return;
            e.stopPropagation();
            if ((_a = array.props) === null || _a === void 0 ? void 0 : _a.disabled)
                return;
            var value = clone((_b = array === null || array === void 0 ? void 0 : array.field) === null || _b === void 0 ? void 0 : _b.value[index]);
            var distIndex = index + 1;
            (_d = (_c = array.field) === null || _c === void 0 ? void 0 : _c.insert) === null || _d === void 0 ? void 0 : _d.call(_c, distIndex, value);
            (_f = (_e = array.props) === null || _e === void 0 ? void 0 : _e.onCopy) === null || _f === void 0 ? void 0 : _f.call(_e, distIndex);
            if (props.onClick) {
                props.onClick(e);
            }
        } })));
});
ArrayBase.Remove = React.forwardRef(function (props, ref) {
    var _a;
    var index = useIndex(props.index);
    var self = useField();
    var array = useArray();
    var prefixCls = usePrefixCls('formily-array-base');
    if (!array)
        return null;
    if (((_a = array.field) === null || _a === void 0 ? void 0 : _a.pattern) !== 'editable')
        return null;
    return (React.createElement(DeleteOutlined, __assign({}, props, { className: cls("".concat(prefixCls, "-remove"), (self === null || self === void 0 ? void 0 : self.disabled) ? "".concat(prefixCls, "-remove-disabled") : '', props.className), ref: ref, onClick: function (e) {
            var _a, _b, _c, _d;
            if (self === null || self === void 0 ? void 0 : self.disabled)
                return;
            e.stopPropagation();
            (_b = (_a = array.field) === null || _a === void 0 ? void 0 : _a.remove) === null || _b === void 0 ? void 0 : _b.call(_a, index);
            (_d = (_c = array.props) === null || _c === void 0 ? void 0 : _c.onRemove) === null || _d === void 0 ? void 0 : _d.call(_c, index);
            if (props.onClick) {
                props.onClick(e);
            }
        } })));
});
ArrayBase.MoveDown = React.forwardRef(function (props, ref) {
    var _a;
    var index = useIndex(props.index);
    var self = useField();
    var array = useArray();
    var prefixCls = usePrefixCls('formily-array-base');
    if (!array)
        return null;
    if (((_a = array.field) === null || _a === void 0 ? void 0 : _a.pattern) !== 'editable')
        return null;
    return (React.createElement(DownOutlined, __assign({}, props, { className: cls("".concat(prefixCls, "-move-down"), (self === null || self === void 0 ? void 0 : self.disabled) ? "".concat(prefixCls, "-move-down-disabled") : '', props.className), ref: ref, onClick: function (e) {
            var _a, _b, _c, _d;
            if (self === null || self === void 0 ? void 0 : self.disabled)
                return;
            e.stopPropagation();
            (_b = (_a = array.field) === null || _a === void 0 ? void 0 : _a.moveDown) === null || _b === void 0 ? void 0 : _b.call(_a, index);
            (_d = (_c = array.props) === null || _c === void 0 ? void 0 : _c.onMoveDown) === null || _d === void 0 ? void 0 : _d.call(_c, index);
            if (props.onClick) {
                props.onClick(e);
            }
        } })));
});
ArrayBase.MoveUp = React.forwardRef(function (props, ref) {
    var _a;
    var index = useIndex(props.index);
    var self = useField();
    var array = useArray();
    var prefixCls = usePrefixCls('formily-array-base');
    if (!array)
        return null;
    if (((_a = array.field) === null || _a === void 0 ? void 0 : _a.pattern) !== 'editable')
        return null;
    return (React.createElement(UpOutlined, __assign({}, props, { className: cls("".concat(prefixCls, "-move-up"), (self === null || self === void 0 ? void 0 : self.disabled) ? "".concat(prefixCls, "-move-up-disabled") : '', props.className), ref: ref, onClick: function (e) {
            var _a, _b, _c;
            if (self === null || self === void 0 ? void 0 : self.disabled)
                return;
            e.stopPropagation();
            (_a = array === null || array === void 0 ? void 0 : array.field) === null || _a === void 0 ? void 0 : _a.moveUp(index);
            (_c = (_b = array === null || array === void 0 ? void 0 : array.props) === null || _b === void 0 ? void 0 : _b.onMoveUp) === null || _c === void 0 ? void 0 : _c.call(_b, index);
            if (props.onClick) {
                props.onClick(e);
            }
        } })));
});
ArrayBase.useArray = useArray;
ArrayBase.useIndex = useIndex;
ArrayBase.useRecord = useRecord;
ArrayBase.mixin = function (target) {
    target.Index = ArrayBase.Index;
    target.SortHandle = ArrayBase.SortHandle;
    target.Addition = ArrayBase.Addition;
    target.Copy = ArrayBase.Copy;
    target.Remove = ArrayBase.Remove;
    target.MoveDown = ArrayBase.MoveDown;
    target.MoveUp = ArrayBase.MoveUp;
    target.useArray = ArrayBase.useArray;
    target.useIndex = ArrayBase.useIndex;
    target.useRecord = ArrayBase.useRecord;
    return target;
};
export default ArrayBase;
//# sourceMappingURL=index.js.map